import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LuChevronLeft, LuChevronRight, LuX } from 'react-icons/lu';
import { useGallery } from '../ProjectFinalModel/useGallery';
import ResultGalleryModal from './ResultGalleryModal';
import useDarkTheme from '../../../utils/useDarkTheme';
import themeImageSource from '../../../utils/themeImageSource';
import '../ProjectFinalModel/style.css';
import './style.css';

const ResultGallery = ({ data, projectId }) => {
  const images = data?.items || [];
  const isDark = useDarkTheme();
  const visibleItems = data?.showAll ? images : images.slice(0, 4);
  const comparisonItems = data?.comparisonItems;
  const isCarouselEnabled = Boolean(data?.carouselEnabled && !comparisonItems);
  const gallery = useGallery(images, {
    keyboardNavigation: !data?.modalEnabled,
  });
  const { closeModal, openModal } = gallery;
  const [isZoomed, setIsZoomed] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [canScrollPrevious, setCanScrollPrevious] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const cardRefs = useRef([]);
  const carouselTrackRef = useRef(null);
  const carouselFrameRef = useRef(null);

  const openSingleModal = useCallback((index) => {
    openModal(index);
  }, [openModal]);

  const closeSingleModal = useCallback((interactionType = 'pointer') => {
    const viewedIndex = gallery.activeIndex;
    closeModal();
    requestAnimationFrame(() => {
      const card = cardRefs.current[viewedIndex];
      if (!card) return;
      if (interactionType === 'keyboard') card.focus();
      else card.blur();
    });
  }, [closeModal, gallery.activeIndex]);

  const syncCarouselPosition = useCallback(() => {
    const track = carouselTrackRef.current;
    if (!track) return;

    const cards = Array.from(track.children);
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const normalizedScrollLeft = Math.min(maxScrollLeft, Math.max(0, track.scrollLeft));
    const nearestIndex = cards.reduce((nearest, card, index) => {
      const cardLeft = card.offsetLeft - track.offsetLeft;
      const nearestLeft = cards[nearest]?.offsetLeft - track.offsetLeft || 0;
      return Math.abs(cardLeft - normalizedScrollLeft) < Math.abs(nearestLeft - normalizedScrollLeft)
        ? index
        : nearest;
    }, 0);

    setCarouselIndex(nearestIndex);
    setCanScrollPrevious(normalizedScrollLeft > 1);
    setCanScrollNext(normalizedScrollLeft < maxScrollLeft - 1);
  }, []);

  const handleCarouselScroll = useCallback(() => {
    if (carouselFrameRef.current) cancelAnimationFrame(carouselFrameRef.current);
    carouselFrameRef.current = requestAnimationFrame(syncCarouselPosition);
  }, [syncCarouselPosition]);

  const scrollCarousel = useCallback((direction) => {
    const track = carouselTrackRef.current;
    if (!track) return;

    const cards = Array.from(track.children);
    const targetIndex = Math.min(
      cards.length - 1,
      Math.max(0, carouselIndex + direction)
    );
    const target = cards[targetIndex];
    if (!target) return;

    track.scrollTo({
      left: target.offsetLeft - track.offsetLeft,
      behavior: 'smooth',
    });
  }, [carouselIndex]);

  useEffect(() => setIsZoomed(false), [gallery.activeIndex, gallery.isOpen]);

  useEffect(() => {
    if (!isCarouselEnabled) return undefined;

    const track = carouselTrackRef.current;
    if (!track) return undefined;

    syncCarouselPosition();
    const resizeObserver = new ResizeObserver(syncCarouselPosition);
    resizeObserver.observe(track);

    return () => {
      resizeObserver.disconnect();
      if (carouselFrameRef.current) cancelAnimationFrame(carouselFrameRef.current);
    };
  }, [isCarouselEnabled, syncCarouselPosition]);

  if (!images.length) return null;

  const { activeImage } = gallery;
  const singleImageModal = data.modalEnabled ? (
    <ResultGalleryModal
      activeIndex={gallery.activeIndex}
      data={data}
      images={images}
      isDark={isDark}
      isOpen={gallery.isOpen}
      onClose={closeSingleModal}
      onIndexChange={gallery.setActiveIndex}
      projectId={projectId}
    />
  ) : null;

  const lightbox = !data.modalEnabled ? (
    <AnimatePresence>
      {gallery.isOpen ? (
        <motion.div className="project-final-model-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          role="dialog" aria-modal="true" aria-label={data.modalLabel || data.title} style={{ '--gallery-glow': gallery.glowColor }}>
          <button className="project-final-model-modal__backdrop" type="button" aria-label="Закрыть" onClick={gallery.closeModal} />
          <motion.div className="project-final-model-modal__panel" initial={{ opacity: 0, scale: 0.965, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.965, y: 16 }}>
            <div className="project-final-model-modal__top">
              <span className="project-final-model-modal__counter">{gallery.activeIndex + 1} / {images.length}</span>
              <button className="project-final-model-modal__close" type="button" aria-label="Закрыть" onClick={gallery.closeModal}><LuX /></button>
            </div>
            <div className={`project-final-model-modal__viewer${isZoomed ? ' is-zoomed' : ''}`}>
              <div className="project-final-model-modal__edge project-final-model-modal__edge--prev">
                <button className="project-final-model-modal__nav project-final-model-modal__nav--prev" type="button"
                  aria-label="Предыдущий слайд" disabled={gallery.isFirstSlide} onClick={gallery.showPrevious}><LuChevronLeft /></button>
              </div>
              <AnimatePresence mode="wait">
                <motion.img key={`${activeImage.id}-${themeImageSource(activeImage, 'full', isDark)}`} className="project-final-model-modal__image"
                  src={themeImageSource(activeImage, 'full', isDark)} alt={activeImage.alt} initial={{ opacity: 0, x: 18 }}
                  animate={{ opacity: 1, x: 0, scale: isZoomed ? 1.45 : 1 }} exit={{ opacity: 0, x: -18 }}
                  drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.22}
                  onClick={() => setIsZoomed((value) => !value)}
                  onDragEnd={(_, info) => {
                    if (info.offset.x > 80 && !gallery.isFirstSlide) gallery.showPrevious();
                    if (info.offset.x < -80 && !gallery.isLastSlide) gallery.showNext();
                  }}
                />
              </AnimatePresence>
              <div className="project-final-model-modal__edge project-final-model-modal__edge--next">
                <button className="project-final-model-modal__nav project-final-model-modal__nav--next" type="button"
                  aria-label="Следующий слайд" disabled={gallery.isLastSlide} onClick={gallery.showNext}><LuChevronRight /></button>
              </div>
              <div className="project-final-model-modal__info"><strong>{activeImage.title}</strong><p>{activeImage.description}</p></div>
            </div>
            <div className="project-final-model-modal__thumbs" ref={gallery.thumbsTrackRef} aria-label="Миниатюры слайдов">
              {images.map((item, index) => (
                <button className={`project-final-model-modal__thumb${index === gallery.activeIndex ? ' is-active' : ''}`}
                  key={item.id} ref={index === gallery.activeIndex ? gallery.activeThumbRef : null} type="button"
                  aria-label={`Открыть слайд ${index + 1}`} onClick={() => gallery.setActiveIndex(index)}>
                  <img src={themeImageSource(item, 'preview', isDark)} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  ) : null;

  const galleryTitle = data.mobileTitleLines?.length
    ? data.mobileTitleLines.map((line) => (
      <span className="result-gallery__mobile-title-line" key={line}>{line}</span>
    ))
    : data.title;

  return (
    <>
      <section className="project-final-model result-gallery" data-project-id={projectId}>
        {isCarouselEnabled ? (
          <div className="result-gallery__heading">
            <div>
              <h2 className="project-final-model__title">{galleryTitle}</h2>
              {data.description ? <p>{data.description}</p> : null}
            </div>
            <span
              className="comparison-gallery__counter result-gallery__counter"
              aria-live="polite"
            >
              {carouselIndex + 1}/{images.length}
            </span>
          </div>
        ) : (
          <>
            <h2 className="project-final-model__title">{galleryTitle}</h2>
            {data.description ? <p>{data.description}</p> : null}
          </>
        )}
        <div className={`${isCarouselEnabled ? 'result-gallery__carousel' : ''}${isCarouselEnabled && data.addGridSpacing ? ' result-gallery__carousel--spaced' : ''}`}>
          <div
            className={`project-final-model__grid result-gallery__grid${isCarouselEnabled ? ' result-gallery__grid--carousel' : ''}${comparisonItems ? ' point-cloud-to-bim__grid' : ''}${!isCarouselEnabled && data.addGridSpacing ? ' result-gallery__grid--spaced' : ''}`}
            ref={isCarouselEnabled ? carouselTrackRef : null}
            onScroll={isCarouselEnabled ? handleCarouselScroll : undefined}
          >
          {comparisonItems ? comparisonItems.map((item, index) => (
            <article className="project-final-model__card" key={item.id}>
              <button className="project-final-model__media" type="button" onClick={() => gallery.openModal(index * 2)}>
                <img src={themeImageSource(item.source, 'preview', isDark)} alt={item.source.alt} loading="lazy" />
              </button>
              <span className="project-final-model__copy"><strong>{data.sourceLabel}</strong><span>{item.title}</span></span>
              <button className="project-final-model__media" type="button" onClick={() => gallery.openModal(index * 2 + 1)}>
                <img src={themeImageSource(item.result, 'preview', isDark)} alt={item.result.alt} loading="lazy" />
              </button>
              <span className="project-final-model__copy"><strong>{data.resultLabel}</strong><span>{item.description}</span></span>
            </article>
          )) : (isCarouselEnabled ? images : visibleItems).map((item, index) => (
            <button
              className="project-final-model__card"
              key={item.id}
              ref={(node) => {
                cardRefs.current[index] = node;
              }}
              type="button"
              aria-label={`${data.openImageLabel || 'Увеличить изображение'} ${index + 1} ${data.ofLabel || 'из'} ${images.length}`}
              onClick={() => data.modalEnabled
                ? openSingleModal(index)
                : gallery.openModal(index)}
            >
              {isCarouselEnabled ? (
                <span className="result-gallery__card-number" aria-hidden="true">
                  {index + 1}
                </span>
              ) : null}
              <span className="project-final-model__media"><img src={themeImageSource(item, 'preview', isDark)} alt={item.alt} loading="lazy" /></span>
              {item.title || item.description ? (
                <span className="project-final-model__copy">
                  {item.title ? <strong>{item.title}</strong> : null}
                  {item.description ? <span>{item.description}</span> : null}
                </span>
              ) : null}
            </button>
          ))}
          </div>
          {isCarouselEnabled ? (
            <>
              <button
                className="result-gallery__nav result-gallery__nav--previous"
                type="button"
                aria-label={data.previousLabel || 'Предыдущая карточка'}
                disabled={!canScrollPrevious}
                onClick={() => scrollCarousel(-1)}
              >
                <LuChevronLeft />
              </button>
              <button
                className="result-gallery__nav result-gallery__nav--next"
                type="button"
                aria-label={data.nextLabel || 'Следующая карточка'}
                disabled={!canScrollNext}
                onClick={() => scrollCarousel(1)}
              >
                <LuChevronRight />
              </button>
            </>
          ) : null}
        </div>
      </section>
      {createPortal(data.modalEnabled ? singleImageModal : lightbox, document.body)}
    </>
  );
};

export default ResultGallery;
